class SNDomainAPI {
  _loadingSignal;
  _loadingSignalResolve;
  _abbreviations = Object.freeze({
    sn: 'sn',
    cgg: 'cgg',
    stt: 'stt',
    imax: 'imax',
  });
  _venueIdMap = Object.freeze({
    sn: 3,
    cgg: 4,
    stt: 4,
    imax: 4,
  });
  _rootLandings = Object.freeze({
    sn: 'https://spaceneedle.com',
    cgg: 'https://chihulygardenandglass.com',
    stt: 'https://seattletoptix.com',
    imax: 'https://seattletoptix.com',
  });

  get loadingSignal() {
    return this._loadingSignal;
  }

  get cmsData() {
    return this._cmsData;
  }

  get appConfig() {
    return this._appConfig;
  }

  /**
   * Private constructor
   */
  constructor() {
    const { promise, resolve, reject } = Promise.withResolvers();
    this._loadingSignal = promise;
    this._loadingSignalResolve = resolve;
    this._loadingSignalReject = reject;
  }

  async init() {
    this._appConfig = await this.fetchAppConfig();
  }

  async fetchAppConfig() {
    try {
      const response = await fetch('/config.json', {
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load app config: HTTP ${response.status}`);
      }

      const jsonResponse = await response.json();

      return jsonResponse;
    } catch (e) {
      throw new Error('Failed to load app config', { cause: e });
    }
  }

  loadCmsData = async (url) => {
    try {
      const response = await fetch(url, {
        headers: {
          Accept: 'application/json',
        },
      });

      this._cmsData = Object.freeze(await response.json());
    } catch (e) {
      throw new Error(`Failed to load CMS data from ${url}`, { cause: e });
    }
  };

  // Dev-only override (docker stack / `vite`): the venue is normally derived
  // from the subdomain (sn./cgg./stt.), which isn't available on localhost.
  // The dev org switcher writes `devOrgOverride` to localStorage so a developer
  // can flip venues without juggling hostnames. Stripped from production builds
  // (import.meta.env.DEV is false there), so it can never affect real traffic.
  _devOrgOverride = () => {
    if (!import.meta.env.DEV) return null;
    try {
      const v = localStorage.getItem('devOrgOverride');
      return Object.keys(this._abbreviations).includes(v) ? v : null;
    } catch {
      return null;
    }
  };

  detectVenue = () => {
    const clientDomain = this._devOrgOverride() ?? window.location.hostname.split('.')[0];
    const client = Object.keys(this._abbreviations).includes(clientDomain) ? clientDomain : this._abbreviations.sn; // modify this, in order to test different venues from localhost

    return {
      venueName: client,
      venueId: this._venueIdMap[client],
      salesTerminalId: this._appConfig.REACT_APP_ORGANIZATION_SALES_CHANNEL_ID[client] || -1,
      landingUrl: this._rootLandings[client],
      crossDomainNavigation: this._appConfig.REACT_APP_CROSS_DOMAIN_NAVIGATION[client],
      venueDisplayName: this._appConfig.REACT_APP_VENUE_NAME[client],
    };
  };

  loadStaticAssets = async () => {
    const { venueName } = this.detectVenue();
    this.applyVenueFavicon(venueName);
    switch (venueName) {
      case 'sn':
        await this.loadCmsData('/cms/latest/sn_config.json');
        break;
      case 'cgg':
        await this.loadCmsData('/cms/latest/cgg_config.json');
        break;
      case 'imax':
      case 'stt':
        await this.loadCmsData('/cms/latest/stt_config.json');
        break;
      default:
        throw new Error(`Unknown venue: ${venueName}`);
    }
  };

  resolveLoad() {
    this._loadingSignalResolve();
  }

  rejectLoad(error) {
    this._loadingSignalReject(error);
  }

  applyVenueFavicon = (venueName) => {
    const href = `/favicons/${venueName}-favicon.ico`;
    let link = document.querySelector('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = href;
    link.type = 'image/x-icon';
  };
}

window.SNDomain = new SNDomainAPI();

(async () => {
  await window.SNDomain.init();
  await window.SNDomain.loadStaticAssets();
})()
  .then(() => {
    window.SNDomain.resolveLoad();
  })
  .catch((error) => {
    window.SNDomain.rejectLoad(error);
  });
